import { defineStore, storeToRefs } from 'pinia'
import { computed, type Raw, ref, shallowRef, watch } from 'vue'

import { useConfig } from '@/composables/config'
import { APP_NATIVE_AD_CLIDS } from '@/constants/general'
import { AdService, type ProviderKey, type ProviderConfig } from '@/modules/adv/ad-service'
import { ConsentManager, type ConsentUiState } from '@/modules/adv/consent-manager'
import { getAdProvidersSettings } from '@/modules/adv/get-ad-providers-settings'
import { HeaderBidding } from '@/modules/adv/header-bidding'
import type { HbSiteGameInfo, HeaderBiddingOptions } from '@/modules/adv/header-bidding/types'
import { initProviders } from '@/modules/adv/provider-initializer'
import { TargetingMapping } from '@/modules/adv/targeting-mapping'
import type { PortalAdTargeting } from '@/modules/adv/types'
import { LogLevel, setAdConsoleLoggerLevel } from '@/modules/adv/utils/ad-console-logger'
import { useAdBlockStore } from '@/store/ad-block-store'
import { useAppStore } from '@/store/app-store'
import { useClientAppStore } from '@/store/client-app-store'
import { useLoggerStore } from '@/store/logger'
import { useUserStore } from '@/store/user-store/user-store'
import type { AdProvidersSettings } from '@/types'
import { getConfigByClid } from '@/utils/clid-config'
import { getHashedEmailFromCookie, updateCookie, YAHOO_ID_SIGNAL_COOKIE } from '@/utils/cookie-parser'
import { sha256hex } from '@/utils/crypto'
import { transformUACtoAdTargetings } from '@/utils/uac-cookie'

import { useNativeAppStore } from '../native-app-store'
import { getCurrentProvidersSettings } from './current-providers-settings'

export const useAdsStore = defineStore('ads', () => {
    const { logEvent, startRumEvent, registerPageHideFallbackEvent } = useLoggerStore()
    const appStore = useAppStore()
    const nativeAppStore = useNativeAppStore()
    const clientAppStore = useClientAppStore()
    const {
        clid,
        isOppoWL,
        platform,
        isDebugMode,
        isGameDistribution,
        isPortalEnv,
        isNativeAppEnv,
        adExpTargeting,
        countryCode,
    } = storeToRefs(appStore)
    const { adBlockStatus } = storeToRefs(useAdBlockStore())

    const nativeBannersEnabled = computed(
        () => appStore.isVersionAtLeast({ android: '1.3.0', ios: '1.1.6' }) && !!APP_NATIVE_AD_CLIDS[clid.value],
    )

    const userStore = useUserStore()

    const adService = shallowRef<Raw<AdService>>()
    const headerBidding = shallowRef<Raw<HeaderBidding>>()
    const consentManager = shallowRef<Raw<ConsentManager>>()
    const consentUiState = ref<ConsentUiState>('unknown')
    let unsubscribeConsentUiState: (() => void) | undefined
    let pendingGameInfo: HbSiteGameInfo | null = null

    const { VUE_APP_AD_MANAGER_ACCOUNT } = useConfig()

    function setAdServiceConfig(options: {
        targeting?: PortalAdTargeting
        auid: string
        isGoogleProvidersEnabled: boolean
        gameDistributionGameId?: string
    }) {
        const clidConfig = getConfigByClid(clid.value)

        const targeting: PortalAdTargeting = {
            playgama_clid: clid.value,
            widget_id: clientAppStore.widgetId || '',
            event_source: 'default',
            exp: adExpTargeting.value,
            [TargetingMapping.AD_BLOCK_STATUS]: adBlockStatus.value,
            [TargetingMapping.IS_LOGGED_IN]: '0',
            ...transformUACtoAdTargetings(clientAppStore.userAcquisitionParams),
            ...options.targeting,
        }

        const headerBiddingConfig: HeaderBiddingOptions = {
            debug: isDebugMode.value,
            platform: platform.value,
            pgAuid: clientAppStore.auid,
            countryCode: countryCode.value,
            adSource: isNativeAppEnv.value ? 'native' : 'portal',
            hashedEmail: getHashedEmailFromCookie(),
            logEvent,
        }

        const providersSettings = getAdProvidersSettings({
            gamAccount: VUE_APP_AD_MANAGER_ACCOUNT,
            appNativeSDK: nativeAppStore.sdk,
            platform: platform.value,
            effectiveType: clientAppStore.effectiveType,
            isVersionAtLeast: appStore.isVersionAtLeast,
            isNativeBannersEnabled: nativeBannersEnabled.value,
            gameDistributionGameId: options.gameDistributionGameId,
        })

        const currentProviders = getCurrentProvidersSettings({
            clid: clid.value,
            providersSettings,
            clidConfig,
            isExportOnWidget: appStore.isExportOnWidget,
            isGameDistribution: isGameDistribution.value,
            isOppoWL: isOppoWL.value,
            isPartnerEmbed: !isPortalEnv.value,
            isNativeAppEnv: isNativeAppEnv.value,
            isNativeBannersEnabled: nativeBannersEnabled.value,
            isGoogleProvidersEnabled: options.isGoogleProvidersEnabled,
        })
        const currentProvidersSettings = Object.entries(currentProviders).reduce<
            Partial<Record<ProviderKey, ProviderConfig[]>>
        >((acc, [key, value]) => {
            acc[key as ProviderKey] = (value as AdProvidersSettings[]).map((s) => ({
                type: s.type,
                timeout: s.options.timeout,
            }))
            return acc
        }, {})

        const providersInitConfig = {
            targeting,
            startRumEvent,
            auid: options.auid,
        }

        if (headerBiddingConfig && !isPortalEnv.value) {
            headerBiddingConfig.adSource = 'export'
        }

        const manager = new ConsentManager({
            handleConsentlessEnv: () => Promise.resolve(countryCode.value === 'US'),
            consentAccount: VUE_APP_AD_MANAGER_ACCOUNT,
            logEvent,
        })

        const service = new AdService({
            targeting,
            startRumEvent,
            logEvent,
            registerPageHideFallbackEvent,
            auid: options.auid,
            waitForHeaderBidding: !!headerBiddingConfig,
            consentReady: manager.ready,
            providersSettings: currentProvidersSettings,
            useFallbackAuctionReuse: true,
        })

        consentManager.value = manager

        unsubscribeConsentUiState?.()
        unsubscribeConsentUiState = manager.onConsentUiStateChange((state) => {
            consentUiState.value = state
        })

        manager.install().then((activeCmps) => {
            const hb = new HeaderBidding(headerBiddingConfig, activeCmps, startRumEvent)
            headerBidding.value = hb
            hb.updateGameInfo(pendingGameInfo)
            service.setHeaderBidding(hb)
        })

        userStore.getUser().then(async () => {
            if (userStore.isCompanyEmployeeLoggedIn) {
                setAdConsoleLoggerLevel(LogLevel.debug)
            }
            if (userStore.user?.email && userStore.user?.email !== '') {
                const email = await sha256hex(userStore.user.email)
                updateCookie(YAHOO_ID_SIGNAL_COOKIE, email, 1000 * 50 * 60 * 24 * 30) // set cookie for 30 days
            }
        })

        adService.value = service

        manager.ready.then(() => {
            Object.entries(currentProviders).forEach(([type, settings]) => {
                initProviders(settings, providersInitConfig, (index, provider) => {
                    service.setProvider(index, provider, type as ProviderKey)
                })
            })
        })
    }

    function updateTargeting(targeting: PortalAdTargeting) {
        adService.value?.updateTargeting(targeting)
    }

    function updateHbGameInfo(gameInfo: HbSiteGameInfo | null) {
        pendingGameInfo = gameInfo
        headerBidding.value?.updateGameInfo(gameInfo)
    }

    watch(adBlockStatus, (status) => {
        updateTargeting({
            [TargetingMapping.AD_BLOCK_STATUS]: status,
        })
    })

    return {
        adService,
        consentManager,
        consentUiState,
        headerBidding,
        nativeBannersEnabled,
        setAdServiceConfig,
        updateTargeting,
        updateHbGameInfo,
    }
})
